import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";

import {
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { getCreateAssessmentInputData } from "Assessments/modules/AssessmentModule";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  reorderEntityFlowResourceMutation,
  createEntityFlowResourceMutation,
  updateEntityFlowResourceMutation,
  deleteEntityFlowResourceMutation,
  repeatEntityFlowResourceMutation,
  importEntityFlowResourceMutation,
  createUnitResourceMutation,
} from "./Mutation";
import { dispatchError, generateRandomId } from "Utils";
import { getNestedChildren, isFolder } from "Utils/EntityFlowUtils";
import {
  getEntireUnitFlowFeedQuery,
  getUnitFlowOutlineFeedQuery,
  getUnitResourcesFeedQuery,
} from "./Query";
import {
  getEntireUnitFlowFeedQueryFromCache,
  getUnitFlowItemFromCache,
  getUnitResourcesFeedQueryFromCache,
  writeUnitFlowItemInCache,
} from "./GraphqlHelper";
import {
  getToastMsgForResourceUpdate,
  getUnitFlowFeedVariablesMemoized,
  getUnitPlanOutlineFeedVariablesMemoized,
  getUnitResourcesVariablesMemoized,
  isUnitFlowRevampEnabled,
  convertToEntityFlowResource,
} from "./utils";

import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { MAX_DEPTH } from "UIComponents/NestedAccordion/constants";

import {
  setActiveContentData,
  setIsActionLoading,
  setMoveToSectionModalData,
  setIsDragDisabled,
} from "../CommonModules";
import {
  convertToCombinedLibraryResource,
  getCombinedLibraryFeedVariablesMemoized,
} from "CombinedLibrary/modules/EntityFlow/utils";
import { getUnitCombinedLibraryQuery } from "CombinedLibrary/modules/EntityFlow/Query";

// function to update childrenHierarchyInfo of all ancestors (bottom to top) of a resource
export const updateAncestorChildCountRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;

  if (node?.childrenHierarchyInfo) {
    node.childrenHierarchyInfo.totalCount += factor;
  }

  if (node?.parent) {
    updateAncestorChildCountRecursively({
      items,
      id: node.parent.id,
      factor,
    });
  }
};

// updates depth recursively
const updateDepthRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;
  node.depth += factor;

  const children = _.get(node, "childrenV2", []);

  _.forEach(children, childNode => {
    updateDepthRecursively({
      items,
      id: childNode.id,
      factor,
    });
  });
};

// recalculates max depth of all ancestors (bottom to top) of a resource
// this is required for drag and drop restrictions
export const recalculateMaxDepth = ({ items, id }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const children = _.get(item, "node.childrenV2", []);
  const childrenItems = _.chain(children)
    .map(child => {
      return _.find(items, ["node.id", child.id]);
    })
    .filter(child => Boolean(child))
    .value();

  const maxDepth =
    children.length == 0
      ? 1
      : _.reduce(
          childrenItems,
          (result, { node }) => {
            return Math.max(
              result,
              _.get(node, "childrenHierarchyInfo.maxDepth", 1) + 1
            );
          },
          0
        );

  if (item?.node?.childrenHierarchyInfo) {
    item.node.childrenHierarchyInfo.maxDepth = maxDepth;
  }

  const parent = _.find(items, ["node.id", item.node.parent?.id]);
  if (parent) {
    recalculateMaxDepth({ items, id: parent.node.id });
  }
};

// when dragging an item after a folder, the putAfterItemId needs to be changed
// because we might need to put the new item after the last child of the folder
const interpolatePutAfterId = ({
  items: _items,
  id,
  putAfterItemId: _putAfterItemId,
  depth,
}) => {
  // remove current item from items
  const items = _.filter(_items, item => item.node.id !== id);
  const itemIndex = _.findIndex(items, ["node.id", _putAfterItemId]);
  const item = items[itemIndex];

  if (!item) return _putAfterItemId;

  let putAfterItemId = _putAfterItemId;

  const isFolder = item.node.resourceType === "FOLDER";
  if (isFolder) {
    const folderDepth = item.node.depth;
    if (depth > folderDepth) {
      putAfterItemId = _putAfterItemId;
    } else {
      let i = itemIndex;
      while (i < items.length) {
        i++;

        const nextItem = items[i];

        const itemDepth = _.get(nextItem, "node.depth");

        if (itemDepth <= depth) {
          const prevItem = items[i - 1];
          const itemId = prevItem?.node?.id;

          putAfterItemId = itemId ?? putAfterItemId;
          break;
        }

        // reached the end, put after the last item
        if (i === items.length - 1) {
          putAfterItemId = nextItem?.node?.id;
          break;
        }
      }
    }
  }

  return putAfterItemId;
};

/**
 * The data in main feed is different than their
 * child nodes, so pick only what is needed
 */
const getChildDataForCache = ({ node }) => {
  // return _.pick(node, ["id", "label", "resourceType", "displaySequence"]);
  return {
    id: node?.id,
  };
};

const removeRepeatedResourcesFromSourceFragment = ({
  itemId,
  variables,
  rollbackResources,
  itemIdsToDelete,
}) => {
  const cachedData = getEntireUnitFlowFeedQueryFromCache(variables);
  const cachedUnitFlowFeed = _.get(
    cachedData,
    "node.unitFlowFeed.unitFlowFlatFeed",
    {}
  );

  // this is the source edge from entire feed cache
  const cachedSourceResourceEdge = findSourceResource({
    unitFlowEdges: cachedUnitFlowFeed.edges,
    itemId,
  });

  // this is the source resource from multinode cache which contains item(attachment/assessment) also
  const cachedSourceResource = getUnitFlowItemFromCache({
    id: _.get(cachedSourceResourceEdge, "node.id", ""),
    variables,
  });

  if (cachedSourceResource) {
    // used to rollback on error
    rollbackResources.push(cachedSourceResource);

    const sourceResource = _.cloneDeep(cachedSourceResource);

    // updating the repeated resources of the source resource
    sourceResource.repeatedResources = _.difference(
      sourceResource.repeatedResources,
      itemIdsToDelete
    );

    // updates the source resource in multinode cache
    writeUnitFlowItemInCache({
      id: sourceResource.id,
      variables,
      data: sourceResource,
    });
  }
};

// will be used to rollback the resources in cache in case of error
export const rollbackResourcesInCache = ({ rollbackResources }) => {
  _.forEach(rollbackResources, resource => {
    writeUnitFlowItemInCache({
      id: resource.id,
      variables: resource.variables,
      data: resource,
    });
  });
};

const getResourceCreationToast = ({
  isCreatingResource = false,
  totalResources = 0,
  isDraggedFromRightPane = false,
  isUploaded = false,
}) => {
  return setToastMsg({
    msg: isCreatingResource
      ? isDraggedFromRightPane
        ? "toastMsgs:action_to_unit_flow"
        : "toastMsgs:action_successfully"
      : "classMaterials:toast_msg_for_section_added",
    locale_params: [
      {
        key: "label",
        value:
          totalResources > 1
            ? "common:resource_label_plural"
            : "common:resource_label",
        isPlainText: false,
      },
      {
        key: "action",
        value:
          isDraggedFromRightPane || !isUploaded
            ? "toastMsgs:added"
            : "toastMsgs:uploaded",
        isPlainText: false,
      },
      {
        key: "count",
        value: totalResources,
        isPlainText: true,
      },
    ],
    type: "success",
    position: "toast-bottom-left",
  });
};

const getAssessmentCreationToast = ({ isDraggedFromRightPane = false }) =>
  setToastMsg({
    msg: isDraggedFromRightPane
      ? "toastMsgs:action_to_unit_flow"
      : "toastMsgs:action_successfully",
    locale_params: [
      {
        key: "label",
        value: "common:task",
        isPlainText: false,
      },
      {
        key: "action",
        value: isDraggedFromRightPane ? "toastMsgs:added" : "toastMsgs:created",
        isPlainText: false,
      },
    ],
    type: "success",
    position: "toast-bottom-left",
  });

export const reorderUnitFlowResources = ({
  id,
  putAfterItemId,
  parentId,
  depth,
  isMovedByDragAndDrop,
}) => {
  return async (dispatch, getState) => {
    if (id == putAfterItemId || id == parentId) {
      // Dropping item to it's original position
      return;
    }
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    let switchParent;
    let resourceType;

    try {
      cache.updateQuery(
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        data => {
          if (!data) return;

          const unitFlowFeed = _.cloneDeep(
            _.get(data, "node.unitFlowFeed.unitFlowFlatFeed", {})
          );

          const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");

          const item = _.find(unitFlowFeed.edges, ({ node }) => node.id === id);
          if (!item) return;

          resourceType = _.get(item, "node.resourceType", "");

          // modify putAfterItemId to reflect the correct position in case of folders
          const _putAfterItemId = interpolatePutAfterId({
            id,
            putAfterItemId,
            items: unitFlowFeed.edges,
            depth,
          });

          // remove the item from its current position and place after putAfterItem
          const newEdges = _.reduce(
            unitFlowFeed.edges,
            (result, edge, i) => {
              // item was dragged and dropped to the first position
              if (i === 0 && !_putAfterItemId) {
                result.push(item);
                result.push(edge);
                return result;
              }

              if (edge.node.id === id) return result;

              // put it after putAfterItem
              if (edge.node.id === _putAfterItemId) {
                result.push(edge);
                result.push(item);
              } else {
                result.push(edge);
              }
              return result;
            },
            []
          );

          unitFlowFeed.edges = newEdges;

          let lastItemId = item.node.id;

          // also move children after the item
          const nestedChildren = getNestedChildren({
            items: _.map(unitFlowFeed.edges, "node"),
            id,
          });

          _.forEach(nestedChildren, child => {
            const childIndex = _.findIndex(
              unitFlowFeed.edges,
              ({ node }) => node.id === child.id
            );

            if (childIndex !== -1) {
              const childItem = _.first(
                unitFlowFeed.edges.splice(childIndex, 1)
              );

              const indexToMove = _.findIndex(unitFlowFeed.edges, {
                node: { id: lastItemId },
              });

              unitFlowFeed.edges.splice(indexToMove + 1, 0, childItem);

              lastItemId = child.id;
            }
          });

          const currentParentId = _.get(item, "node.parent.id");
          switchParent = parentId !== currentParentId;

          // adding this condition for null and undefined comparisons
          if (_.isEmpty(currentParentId) && _.isEmpty(parentId)) {
            switchParent = false;
          }

          // remove from current parent's children and put in new parent's children
          if (switchParent) {
            const currentParent = _.find(unitFlowFeedNodes, {
              id: currentParentId,
            });

            if (currentParent) {
              currentParent.childrenV2 = _.filter(
                currentParent.childrenV2,
                ({ id }) => id !== id
              );
              updateAncestorChildCountRecursively({
                id: currentParent.id,
                items: unitFlowFeed.edges,
                factor: -(
                  (isFolder(item.node) ? 0 : 1) +
                  _.get(item, "node.childrenHierarchyInfo.totalCount", 0)
                ),
              });

              recalculateMaxDepth({
                items: unitFlowFeed.edges,
                id: currentParent.id,
              });
            }

            if (parentId) {
              item.node.parent = {
                ...(item.node.parent ?? {}),
                id: parentId,
              };

              const parent = _.find(unitFlowFeedNodes, { id: parentId });
              if (parent) {
                parent.childrenV2.push(_.get(item, "node"));
                updateAncestorChildCountRecursively({
                  id: parentId,
                  items: unitFlowFeed.edges,
                  factor:
                    (isFolder(item.node) ? 0 : 1) +
                    _.get(item, "node.childrenHierarchyInfo.totalCount", 0),
                });
              }
            } else item.node.parent = null;
          }

          const currentDepth = _.get(item, "node.depth", 1);
          const factor = depth - currentDepth;

          updateDepthRecursively({
            items: unitFlowFeed.edges,
            id: item.node.id,
            factor,
          });

          recalculateMaxDepth({
            items: unitFlowFeed.edges,
            id: item.node.id,
          });

          return update(data, {
            node: {
              unitFlowFeed: {
                unitFlowFlatFeed: { $set: unitFlowFeed },
              },
            },
          });
        }
      );

      dispatch(setMoveToSectionModalData({}));

      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
        filters,
        userInfo,
        curriculumType,
      });

      await client.mutate({
        mutation: reorderEntityFlowResourceMutation,
        variables: {
          input: {
            id,
            putAfterItemId,
            parentResourceId: parentId,
            switchParent,
          },
        },

        refetchQueries: [
          {
            query: getUnitFlowOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
      });

      if (!isMovedByDragAndDrop) {
        let title;
        switch (resourceType) {
          case "FOLDER":
            title = "common:folder";
            break;
          case "ASSIGNMENT":
          case "ASSESSMENT": {
            title = "toastMsgs:assignment";
            break;
          }
          default:
            title = `toastMsgs:resource`;
        }

        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:label_successfully_moved",
            locale_params: [
              {
                key: "label",
                value: title,
                isPlainText: false,
              },
            ],
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

const findSourceResource = ({ unitFlowEdges, itemId }) => {
  return _.find(unitFlowEdges, edge => {
    const { node } = edge;
    return node.isRepeated === false && _.get(node, "item.id") === itemId;
  });
};

export const createUnitResource = ({ resources }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      searchText,
    });

    const cachedData = getUnitResourcesFeedQueryFromCache(feedQueryVariables);
    const unitResourceId = _.get(
      cachedData,
      "node.unitFlowFeed.unitResourceId",
      ""
    );

    try {
      const variables = {
        input: {
          parentId: null,
          putAfterItemId: null,
          entityFlowId: unitResourceId,
          resources,
        },
      };

      await client.mutate({
        mutation: createUnitResourceMutation,
        variables,
        update: (
          cache,
          {
            data: {
              planner: { createEntityFlowResource },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResourcesEdges = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
              );

              const updatedEdges = [
                {
                  node: createEntityFlowResource[0],
                  __typename: "EntityFlowResourceEdge",
                },
                ...unitResourcesEdges,
              ];
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateUnitResources = ({ id, label, attachment }) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      searchText,
    });

    const variables = {
      input: {
        id,
        label,
        attachment,
      },
    };

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        unitFlowfeedQueryVariables
      );
      const unitFlowFeed = _.cloneDeep(
        _.get(unitFlowFeedData, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
      );
      _.forEach(unitFlowFeed, edge => {
        const { node } = edge;
        if (node.item?.id !== id) return;

        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id: node.id,
          variables: unitFlowfeedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          if (unitFlowNode?.item?.attachment) {
            const nodeAttachment = unitFlowNode.item.attachment;
            nodeAttachment.name = label;
            if (nodeAttachment.attachmentType === "LINK") {
              nodeAttachment.url = attachment.url;
              nodeAttachment.thumbUrl = attachment.thumbUrl;
            } else if (nodeAttachment.attachmentType === "NOTE") {
              nodeAttachment.name = label;
              nodeAttachment.attachmentTitle = label;
              nodeAttachment.title = label;
            } else {
              nodeAttachment.name = label;
            }
          }

          writeUnitFlowItemInCache({
            id: node.id,
            variables: unitFlowfeedQueryVariables,
            data: unitFlowNode,
          });
        }
      });

      await client.mutate({
        mutation: updateEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateEntityFlowResource: {
              id,
              __typename: "EntityFlowResource",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
              );
              const itemIdx = _.findIndex(
                unitResources,
                ({ node }) => node.id === id
              );

              const newItem = _.cloneDeep(unitResources[itemIdx]);

              if (newItem?.node) {
                newItem.node.label = label;
                newItem.node.attachment = {
                  ...newItem.node.attachment,
                  ...attachment,
                };

                return update(data, {
                  node: {
                    unitFlowFeed: {
                      unitFlowFlatFeed: {
                        edges: {
                          [itemIdx]: { $set: newItem },
                        },
                      },
                    },
                  },
                });
              }
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node?.item?.id === id) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate("FILE")));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteUnitResources = ({ ids }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      searchText,
    });

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });
    let countOfDeletedItems = 0;

    try {
      await client.mutate({
        mutation: deleteEntityFlowResourceMutation,
        variables: { input: { ids } },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteEntityFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
              );

              const updatedEdges = _.filter(
                unitResources,
                ({ node }) => !_.includes(ids, node.id)
              );

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed")
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");
              const itemIdsToDelete = _.map(
                _.filter(unitFlowFeed?.edges, ({ node }) => {
                  const { item } = node;
                  return item && _.includes(ids, item?.id);
                }),
                ({ node }) => node.id
              );

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const createUnitFlowResources = ({
  resources,
  putAfterItemId: _putAfterItemId = null,
  setNewDataIds,
  isFolderCollapsed: _isFolderCollapsed = false,
  quickCreation = false,
  sectionCreationType,
  showLoader = false,
  parentId,
  newDepth: _newDepth,
  isAssessmentImport = false,
  isImportFromUnit = false,
  rightPaneItem = null, // only be used for right pane items
  showToast = true,
  shouldAddToBottom = false,
  unitFlowId: _unitFlowId,
  isImportedInLibrary = false,
  showRightPaneLoader = false,
  setIsCombinedLibraryActionLoading = () => {},
}) => {
  return async (dispatch, getState) => {
    if (_.isEmpty(resources)) return;
    const state = getState();

    const defaultPositioningOfNewItems = _.get(
      state,
      "unitFlow.displaySettings.add_to_top",
      false
    );

    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;

    const addToTop =
      _putAfterItemId === null && (Boolean(rightPaneItem) || _addToTop);

    const isInlineCreateOrEdit = _.get(
      state,
      "unitFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );
    const isCreatingAssessment = _.some(resources, {
      itemType: "ASSESSMENT",
    });
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const isDraggedFromRightPane = !_.isEmpty(rightPaneItem);

    const resource = _.first(resources);
    const resourceType = _.get(resource, "itemType", "");

    // give all conditions when you don't need to refetch outline feed
    const skipRefetchQueries = isImportedInLibrary;

    // currently optimistic response will be shown only for right pane items
    const showOptimisticResponse = rightPaneItem ? true : false;

    const filters = _.get(state, "unitFlow.filters", {});
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    // variables for updating right pane
    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
      }
    );

    if (showLoader) {
      dispatch(setIsActionLoading(true));
    } else if (showRightPaneLoader) {
      setIsCombinedLibraryActionLoading(true);
    }

    if (showOptimisticResponse) {
      dispatch(setIsDragDisabled(true));
    }

    try {
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const rightPaneQueryData = cache.readQuery({
        query: getUnitCombinedLibraryQuery,
        variables: combinedLibraryFeedQueryVariables,
      });

      const unitFlowIdFromRightPane = _.get(
        rightPaneQueryData,
        "node.unitFlowFeed.unitFlowId",
        ""
      );
      const unitFlowId =
        _.get(cachedData, "node.unitFlowFeed.unitFlowId") ||
        unitFlowIdFromRightPane ||
        _unitFlowId;

      if (!unitFlowId) {
        if (isInlineCreateOrEdit) {
          dispatch(setActiveContentData({}));
        }
        return;
      }

      let unitFlowFeed = _.cloneDeep(
        _.get(cachedData, "node.unitFlowFeed.unitFlowFlatFeed", {})
      );
      const unitFlowFeedCopy = _.cloneDeep(
        _.get(cachedData, "node.unitFlowFeed.unitFlowFlatFeed", {})
      );

      let putAfterItemId, isFolderCollapsed;

      putAfterItemId = _putAfterItemId;
      isFolderCollapsed = _isFolderCollapsed;

      if (isCreatingAssessment) {
        const isInlineCreation = _.get(
          state,
          "unitFlow.activeContentData.isInlineCreateOrEditItem",
          false
        );

        let itemState;
        switch (true) {
          case isInlineCreation:
            itemState = "activeContentData";
            break;
          case isAssessmentImport:
            itemState = "assessmentImportModalData";
            break;
          case isImportFromUnit:
            itemState = "assessmentSelectModalData";
            break;
          default:
            itemState = "assignmentModalData";
        }

        putAfterItemId = _.get(
          state,
          `unitFlow.${itemState}.putAfterItemId`,
          putAfterItemId
        );

        isFolderCollapsed = _.get(
          state,
          `unitFlow.${itemState}.isFolderCollapsed`,
          isFolderCollapsed
        );
      }

      const putAfterItem = _.find(
        unitFlowFeed?.edges,
        ({ node }) => node.id === putAfterItemId
      )?.node;

      const isSection = isFolder(putAfterItem);

      // when creating a section in parallel to current
      const isParallelSection = sectionCreationType === "PARALLEL";

      // first data will be created by UI
      let isOptimisticResponseData = showOptimisticResponse;

      // If the parent and putAfterItem are the same, then the new item should be created inside the parent
      // only valid for right pane items drop
      const parentSameAsPutAfterItem = parentId === putAfterItemId;

      // when creating a subsection to the current section which was previously collapsed
      const isExpandedSubsection =
        sectionCreationType === "EXPANDED_SUBSECTION";

      // if putAfterItem is a section, then newly created resources are meant to go inside it
      // otherwise, we find the parent of putAfterItem
      let parent = putAfterItem;

      // if folder is collapsed, new item should be created after it
      const shouldPutInsideSection =
        isSection &&
        !isFolderCollapsed &&
        !isParallelSection &&
        (parentSameAsPutAfterItem || !showOptimisticResponse);

      if (!shouldPutInsideSection) {
        const newParentId =
          isParallelSection || (isExpandedSubsection && parentId)
            ? parentId
            : putAfterItem?.parent?.id;

        parent = _.find(
          unitFlowFeed?.edges,
          ({ node }) => node.id === newParentId
        )?.node;
      }

      const parentResourceId = shouldPutInsideSection
        ? putAfterItemId
        : parent?.id;

      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
        filters,
        userInfo,
        curriculumType,
      });

      const variables = {
        input: {
          parentId: parentResourceId || null,
          putAfterItemId,
          entityFlowId: unitFlowId,
          resources: resources,
          addToTop,
        },
        filters: _.get(feedQueryVariables, "filters", {}),
      };

      if (isImportedInLibrary) {
        variables.portalType = "PLANNER";
      }

      let optimisticNode = {};
      if (showOptimisticResponse) {
        optimisticNode = convertToEntityFlowResource({
          resource: rightPaneItem,
          parent,
          unitFlowFeed,
        });

        /*
          Writes the optimistic node in cache
          Prevents skeleton in case of optimstic response
        */
        writeUnitFlowItemInCache({
          id: optimisticNode.id,
          variables: feedQueryVariables,
          data: optimisticNode,
        });
      }

      const response = await client.mutate({
        mutation: createEntityFlowResourceMutation,
        variables,
        ...(showOptimisticResponse
          ? {
              optimisticResponse: {
                __typename: "Mutation",
                planner: {
                  createEntityFlowResource: [optimisticNode],
                  __typename: "PlannerMutations",
                },
              },
            }
          : {}),
        awaitRefetchQueries: true,
        refetchQueries: skipRefetchQueries
          ? []
          : [
              {
                query: getUnitFlowOutlineFeedQuery,
                variables: outlineQueryVariables,
              },
            ],
        update: (
          cache,
          {
            data: {
              planner: { createEntityFlowResource },
            },
          }
        ) => {
          if (!isFolder(_.first(createEntityFlowResource))) {
            cache.updateQuery(
              {
                query: getUnitCombinedLibraryQuery,
                variables: combinedLibraryFeedQueryVariables,
              },
              data => {
                if (!data) return;
                const oldEdges = _.cloneDeep(
                  _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
                );
                let updatedEdges = [];
                // if the resource is added from rightPane no need to add again in rightPane
                if (rightPaneItem) return;
                const newEdges = _.map(createEntityFlowResource, resource => ({
                  node: convertToCombinedLibraryResource({ resource }),
                  __typename: "EntityFlowResourceEdge",
                }));
                updatedEdges = [...newEdges, ...oldEdges];

                return update(data, {
                  node: {
                    unitFlowFeed: {
                      unitFlowFlatFeed: {
                        edges: { $set: updatedEdges },
                      },
                    },
                  },
                });
              }
            );
          }
          if (_.isEmpty(unitFlowFeed) || _.isEmpty(createEntityFlowResource))
            return;
          // only do these operations if this is a optimistic case
          if (showOptimisticResponse && !isOptimisticResponseData) {
            unitFlowFeed = unitFlowFeedCopy;
            if (parent) {
              parent = _.find(unitFlowFeed.edges, {
                node: { id: parent.id },
              })?.node;
            }
          }

          let newDepth = 1;
          if (putAfterItem) {
            newDepth = putAfterItem.depth;
            if (shouldPutInsideSection) newDepth += 1;
          }

          if (isParallelSection && parent.depth) {
            newDepth = parent.depth + 1;
          }

          newDepth = _newDepth ?? newDepth;

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const createdResources = _.map(
                _.cloneDeep(createEntityFlowResource),
                resource => ({
                  __typename: "EntityFlowResourceEdge",
                  node: {
                    __typename: "EntityFlowResource",
                    ...resource,
                    ...(isFolder(resource) &&
                    _.isEmpty(resource.childrenHierarchyInfo)
                      ? {
                          childrenHierarchyInfo: {
                            maxDepth: 1,
                            totalCount: 0,
                            totalCountWithFolder: 0,
                            __typename: "EntityFlowResourceHierarchyInfo",
                          },
                        }
                      : {}),
                    depth: resource.depth ?? newDepth,
                  },
                })
              );

              if (putAfterItemId && parent) {
                const createdResourcesAsChild = _.map(
                  createdResources,
                  resource => {
                    const { node } = resource;

                    return {
                      __typename: "EntityFlowResource",
                      ...getChildDataForCache({ node }),
                    };
                  }
                );

                parent.childrenV2 = [
                  ...createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                const factor = _.reduce(
                  createdResources,
                  (acc, resource) => acc + (isFolder(resource.node) ? 0 : 1),
                  0
                );

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor,
                });
              }
              const hasNextPage = _.get(unitFlowFeed, "pageInfo.hasNextPage");

              // if put after item is present, then add in edges after the id
              if (putAfterItemId) {
                let index = _.findIndex(
                  unitFlowFeed.edges,
                  ({ node }) => node.id === putAfterItemId
                );

                if (
                  isSection &&
                  (isFolderCollapsed || putAfterItem.depth >= newDepth)
                ) {
                  const feedLength = unitFlowFeed.edges.length;

                  for (let i = index + 1; i < feedLength; i++) {
                    const item = unitFlowFeed.edges[i];
                    if (item.node.depth <= newDepth) {
                      index = i - 1;
                      break;
                    }

                    // edge case: this is the last folder in the feed
                    if (i === feedLength - 1) {
                      index = i;
                      break;
                    }
                  }
                }

                unitFlowFeed.edges.splice(index + 1, 0, ...createdResources);
              } else {
                // add to the top if it is dragged from the rightPane to the first position
                // otherwise add to the last position
                if (!hasNextPage) {
                  const edges = unitFlowFeed.edges;
                  unitFlowFeed.edges = addToTop
                    ? [...createdResources, ...edges]
                    : [...edges, ...createdResources];
                }
              }

              if (parent) {
                recalculateMaxDepth({
                  items: unitFlowFeed.edges,
                  id: parent.id,
                });
              }

              // update source resource if we are repeating resources
              _.forEach(createdResources, resourceEdge => {
                const resource = _.get(resourceEdge, "node", {});
                const { id, isRepeated, item } = resource;

                if (isRepeated) {
                  const sourceResourceEdge = findSourceResource({
                    unitFlowEdges: unitFlowFeed.edges,
                    itemId: item.id,
                  });

                  if (sourceResourceEdge) {
                    sourceResourceEdge.node.repeatedResources.push(id);
                  }
                }
              });

              const updatedFeed = {
                ...unitFlowFeed,
                resourceBasedCount: {
                  ...unitFlowFeed.resourceBasedCount,
                  totalCount:
                    _.get(unitFlowFeed, "resourceBasedCount.totalCount", 0) +
                    createdResources?.length,
                },
              };

              setNewDataIds?.(_.map(createEntityFlowResource, "id", []));
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );

          setTimeout(() => {
            if (isInlineCreateOrEdit) {
              dispatch(setActiveContentData({}));
            }

            if (
              !quickCreation &&
              !isImportFromUnit &&
              !isAssessmentImport &&
              !isOptimisticResponseData
            ) {
              if (isCreatingAssessment && showToast) {
                dispatch(
                  getAssessmentCreationToast({ isDraggedFromRightPane })
                );
              } else {
                const resourceInput = resource?.itemInput;
                const isCreatingResource =
                  _.has(resourceInput, "attachment") || isDraggedFromRightPane;
                const totalResources = _.size(resources);
                const isUploaded =
                  !isDraggedFromRightPane &&
                  _.has(resourceInput?.attachment, "metadata");

                dispatch(
                  getResourceCreationToast({
                    isCreatingResource,
                    totalResources,
                    isDraggedFromRightPane,
                    isUploaded,
                  })
                );
              }
            }

            isOptimisticResponseData = false;
          });
        },
      });

      return response;
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
      if (showOptimisticResponse) {
        dispatch(setIsDragDisabled(false));
      }
      dispatch(setIsActionLoading(false));
      if (showRightPaneLoader) {
        dispatch(setIsCombinedLibraryActionLoading(false));
      }
    }
  };
};

export const updateUnitFlowResources = ({
  id,
  label,
  attachment,
  resourceType,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating unit flow feed
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const isInlineCreateOrEdit = _.get(
      state,
      "unitFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
      unitPlanId,
    });

    const isResource = _.includes(["NOTE", "LINK", "FILE"], resourceType);

    const variables = {
      input: {
        id,
        label,
      },
    };

    if (attachment) {
      if (_.includes(["LINK", "NOTE"], resourceType)) {
        variables.input.attachment = attachment;
      } else {
        variables.input.attachment = {
          type: _.get(attachment, "type", "FILE"),
          name: label,
        };
      }
    }

    // variables for updating right pane resources
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const unitResourceFeedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      searchText,
    });

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const unitFlowFeed = _.cloneDeep(
        _.get(unitFlowFeedData, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
      );

      const originalNode = _.find(unitFlowFeed, ({ node }) => node.id === id);
      const itemId = _.get(originalNode, "node.item.id", "");

      if (isResource) {
        _.forEach(unitFlowFeed, edge => {
          const { node } = edge;
          if (node.item?.id !== itemId) return;

          const cachedUnitFlowNode = getUnitFlowItemFromCache({
            id: node.id,
            variables: feedQueryVariables,
          });

          if (cachedUnitFlowNode) {
            // used to rollback on error
            rollbackResources.push(cachedUnitFlowNode);

            const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

            unitFlowNode.label = label;

            if (unitFlowNode?.item?.attachment) {
              const nodeAttachment = unitFlowNode.item.attachment;
              nodeAttachment.name = label;
              if (nodeAttachment.attachmentType === "LINK") {
                nodeAttachment.url = attachment.url;
                nodeAttachment.thumbUrl = attachment.thumbUrl;
              } else if (nodeAttachment.attachmentType === "NOTE") {
                nodeAttachment.name = label;
                nodeAttachment.attachmentTitle = label;
                nodeAttachment.title = label;
              } else {
                nodeAttachment.name = label;
              }
            }

            writeUnitFlowItemInCache({
              id: node.id,
              variables: feedQueryVariables,
              data: unitFlowNode,
            });
          }
        });
      } else {
        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id,
          variables: feedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          writeUnitFlowItemInCache({
            id: id,
            variables: feedQueryVariables,
            data: unitFlowNode,
          });
        }
      }

      dispatch(setActiveContentData({}));

      await client.mutate({
        mutation: updateEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateEntityFlowResource: {
              id,
              __typename: "EntityFlowResource",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node?.item?.id == itemId) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          /*
            if resource update right pane resources pane
            if section update outline 
          */
          if (isResource) {
            cache.updateQuery(
              {
                query: getUnitResourcesFeedQuery,
                variables: unitResourceFeedQueryVariables,
              },
              data => {
                if (!data) return;

                const unitResources = _.cloneDeep(
                  _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", {})
                );
                const itemIdx = _.findIndex(
                  unitResources,
                  ({ node }) => node.attachment?.id === attachment.id
                );

                const newItem = _.cloneDeep(unitResources[itemIdx]);

                if (newItem?.node) {
                  newItem.node.label = label;
                  newItem.node.attachment = {
                    ...newItem.node.attachment,
                    ...attachment,
                  };

                  return update(data, {
                    node: {
                      unitFlowFeed: {
                        unitFlowFlatFeed: {
                          edges: {
                            [itemIdx]: { $set: newItem },
                          },
                        },
                      },
                    },
                  });
                }
              }
            );
          } else {
            cache.updateQuery(
              {
                query: getUnitFlowOutlineFeedQuery,
                variables: outlineQueryVariables,
              },
              data => {
                if (!data) return;

                const unitFlowFeedOutline = _.cloneDeep(
                  _.get(data, "node.unitFlowFeed.unitFlowOutline", [])
                );
                const itemIdx = _.findIndex(unitFlowFeedOutline, { id });

                const item = unitFlowFeedOutline[itemIdx];
                if (item) {
                  const newItem = _.cloneDeep(item);

                  newItem.label = label;

                  return update(data, {
                    node: {
                      unitFlowFeed: {
                        unitFlowOutline: {
                          [itemIdx]: { $set: newItem },
                        },
                      },
                    },
                  });
                } else return data;
              }
            );
          }
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate(resourceType)));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
    }
  };
};

export const deleteUnitFlowResources = ({
  ids: _ids,
  shouldDeleteSectionContents = false,
  deleteLinkedCopies = false,
  repeatedResources = [],
  isSection,
  groupType = "LEARNING_ENGAGEMENT",
}) => {
  return async (dispatch, getState) => {
    const ids = _.compact(_ids);
    if (_.isEmpty(ids)) return;

    const uniqueAssessmentSetIds = new Set();
    const uniqueUnitResourceIds = new Set();

    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const filters = _.get(state, "unitFlow.filters", {});

    const refetchQueries = [];

    if (isSection) {
      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
      });

      refetchQueries.push({
        query: getUnitFlowOutlineFeedQuery,
        variables: outlineQueryVariables,
      });
    }

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const cachedData = getEntireUnitFlowFeedQueryFromCache(feedQueryVariables);

    const cachedUnitFlowFeed = _.get(
      cachedData,
      "node.unitFlowFeed.unitFlowFlatFeed",
      {}
    );

    const cachedUnitFlowFeedNodes = _.map(cachedUnitFlowFeed.edges, "node");

    const resource = getUnitFlowItemFromCache({
      id: _.first(ids),
      variables: feedQueryVariables,
    });

    const resourceType = _.get(resource, "resourceType", "");
    const isRepeatedResource = _.get(resource, "isRepeated", false);

    // pushes all the siblings of the resource to ids
    if (deleteLinkedCopies) {
      const id = _.get(resource, "id", "");
      const resourceItemId = _.get(resource, "item.id", "");

      _.forEach(cachedUnitFlowFeed.edges, ({ node }) => {
        const isRepeated = _.get(node, "isRepeated", false);
        const itemId = _.get(node, "item.id", "");
        if (isRepeated && itemId === resourceItemId && id !== node.id) {
          ids.push(node.id);
        }
      });
    }

    const itemIdsToDelete = [...ids, ...repeatedResources];

    // will contain resources which are going to be roll backed in case of error
    const rollbackResources = [];

    // if the resource is repeated, then we need to update the source resource
    if (isRepeatedResource) {
      removeRepeatedResourcesFromSourceFragment({
        itemId: _.get(resource, "item.id", ""),
        variables: feedQueryVariables,
        rollbackResources,
        itemIdsToDelete,
      });
    }

    // this will contain assessments list with their updated unitFlowResources
    const newRightPaneAssessmentsUnitFlowResources = [];

    // if resource is a source assessment itself
    if (resourceType == "ASSESSMENT" && !isRepeatedResource) {
      const assessmentId = _.get(resource, "item.id", "");

      newRightPaneAssessmentsUnitFlowResources.push({
        id: assessmentId,
        newUnitFlowResources: [],
      });
      uniqueAssessmentSetIds.add(assessmentId);
    } else if (resourceType === "ENTITY_FLOW_RESOURCE" && !isFolder(resource)) {
      const unitResourceId = _.get(resource, "item.id", "");
      uniqueUnitResourceIds.add(unitResourceId);
    }

    if (isFolder(resource) && shouldDeleteSectionContents) {
      const deletedItems = getNestedChildren({
        items: cachedUnitFlowFeedNodes,
        id: resource.id,
      });

      const nestedChildrenIds = _.map(deletedItems, "id");

      _.forEach(deletedItems, item => {
        const resourceType = _.get(item, "resourceType", "");

        if (resourceType == "ASSESSMENT") {
          const repeatedResourcesIds = _.get(item, "repeatedResources", []);
          const isRepeated = _.get(item, "isRepeated", false);

          const remainingRepeatedResourcesIds = _.difference(
            repeatedResourcesIds,
            nestedChildrenIds
          );

          // means this is the source assessment with repeatedResources in it
          const assessmentId = _.get(item, "item.id", "");
          if (!isRepeated) {
            // push the new repeated resources of the new source assessment
            newRightPaneAssessmentsUnitFlowResources.push({
              id: assessmentId,
              newUnitFlowResources: _.map(
                remainingRepeatedResourcesIds,
                id => ({
                  id,
                })
              ),
            });
            uniqueAssessmentSetIds.add(assessmentId);
          } else {
            removeRepeatedResourcesFromSourceFragment({
              itemId: assessmentId,
              variables: feedQueryVariables,
              rollbackResources,
              itemIdsToDelete: [item.id],
            });
          }

          if (!_.isEmpty(remainingRepeatedResourcesIds)) {
            // take first item as new source as it's the most recent and will become our new source
            const newSourceId = remainingRepeatedResourcesIds.shift();
            uniqueAssessmentSetIds.delete(assessmentId);

            const cachedNewSource = getUnitFlowItemFromCache({
              id: newSourceId,
              variables: feedQueryVariables,
            });

            if (cachedNewSource) {
              // used to rollback on error
              rollbackResources.push(cachedNewSource);

              const newSource = _.cloneDeep(cachedNewSource);

              newSource.isRepeated = false;
              newSource.repeatedResources = remainingRepeatedResourcesIds;

              writeUnitFlowItemInCache({
                id: newSourceId,
                variables: feedQueryVariables,
                data: newSource,
              });
            }
          }
        } else {
          const unitResourceId = _.get(item, "item.id", "");
          uniqueUnitResourceIds.add(unitResourceId);
        }
      });
    }

    let countOfDeletedItems = 0;
    const rightPaneDeleteItemIds = [];

    try {
      await client.mutate({
        mutation: deleteEntityFlowResourceMutation,
        variables: {
          input: {
            ids,
            removeWithChildren: shouldDeleteSectionContents,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteEntityFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        refetchQueries,
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed")
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }

                const nestedChildren = getNestedChildren({
                  items: unitFlowFeedNodes,
                  id: item.id,
                });

                if (isFolder(item)) {
                  if (shouldDeleteSectionContents) {
                    const nestedChildrenIds = _.map(nestedChildren, "id");
                    unitFlowFeed.edges = _.filter(
                      unitFlowFeed.edges,
                      ({ node }) => !_.includes(nestedChildrenIds, node.id)
                    );

                    if (parent) {
                      updateAncestorChildCountRecursively({
                        id: parent.id,
                        items: unitFlowFeed.edges,
                        factor: -_.get(
                          item,
                          "childrenHierarchyInfo.totalCount",
                          0
                        ),
                      });
                    }
                  } else {
                    // children should be pushed outwards after parent deletion
                    // so we decrease depth and change parent
                    _.forEach(nestedChildren, child => {
                      child.depth -= 1;

                      // only update parent of immediate children
                      // otherwise the remaining structure will come out flat
                      if (parent && child.parent?.id === item.id) {
                        parent.childrenV2.push(
                          getChildDataForCache({ node: child })
                        );

                        child.parent = {
                          ...(child.parent ?? {}),
                          id: parent.id,
                        };
                      }
                    });
                  }
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              const rightPaneItems = _.get(
                state,
                "unitFlow.rightPaneItems",
                []
              );
              const combinedLibraryUniqueIds = new Set([
                ...uniqueAssessmentSetIds,
                ...uniqueUnitResourceIds,
              ]);

              const idsInRightPane = new Set();
              _.forEach(rightPaneItems, item => {
                const resourceType = _.get(item, "resourceType", "");
                const id =
                  resourceType === "ASSESSMENT"
                    ? _.get(item, "item.id", "")
                    : _.get(item, "id", "");
                idsInRightPane.add(id);
              });

              const intersection = _.intersection(
                _.toArray(idsInRightPane),
                _.toArray(combinedLibraryUniqueIds)
              );

              if (!_.isEmpty(intersection)) {
                _.forEach(intersection, id => {
                  if (
                    !_.find(
                      updatedFeed.edges,
                      ({ node }) => node.item?.id == id
                    )
                  )
                    rightPaneDeleteItemIds.push(id);
                });
              }

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
          const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
            {
              unitPlanId,
              filters: _.get(state, "unitPlans.combinedLibrary.filters", {}),
            }
          );

          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: combinedLibraryFeedQueryVariables,
            },
            data => {
              if (!data) return;
              const oldEdges = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
              );
              const updatedEdges = _.filter(
                oldEdges,
                ({ node }) => !_.includes(rightPaneDeleteItemIds, node.item.id)
              );
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);

      // Revert optimistic data on error
      cache.updateQuery(
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        () => cachedData
      );

      // Revert resources on error
      rollbackResourcesInCache({ rollbackResources });

      dispatchError({ dispatch, e: error });
    }
  };
};

/**
 * This function removes deleted assignments from cache for both middlePane and rightPane
 * Used when an assessment / LE / worksheet is discarded on creation modal
 */
export const removeUnitFlowResourcesFromFeed = ({ ids }) => {
  return (dispatch, getState) => {
    const state = getState();

    const currentTab = _.get(state, "unitPlans.currentTab");
    const isUnitFlowTab = currentTab === "unitFlow";
    const isCombinedLibraryOpen =
      _.get(state, "unitPlans.customRightPane") === "COMBINED_LIBRARY";

    if (
      (!isUnitFlowTab && !isCombinedLibraryOpen) ||
      !isUnitFlowRevampEnabled() ||
      _.isEmpty(ids)
    ) {
      return;
    }

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    cache.updateQuery(
      {
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
      },
      data => {
        if (!data) return;

        let countOfDeletedItems = 0;

        const unitFlowFeed = _.cloneDeep(
          _.get(data, "node.unitFlowFeed.unitFlowFlatFeed")
        );

        const unitFlowFeedEdges = unitFlowFeed.edges;
        const unitFlowFeedNodes = _.map(unitFlowFeedEdges, "node");

        const items = _.filter(unitFlowFeedNodes, node => {
          const itemId = _.get(node, "item.id");
          return _.includes(ids, itemId);
        });

        _.forEach(items, item => {
          const parent = _.find(
            unitFlowFeedNodes,
            ({ id }) => id === item.parent?.id
          );

          if (parent) {
            parent.childrenV2 = _.filter(
              parent.childrenV2,
              ({ id }) => id !== item.id
            );

            updateAncestorChildCountRecursively({
              id: parent.id,
              items: unitFlowFeedEdges,
              factor: -1,
            });

            recalculateMaxDepth({
              items: unitFlowFeedEdges,
              id: parent.id,
            });
          }
        });

        countOfDeletedItems = _.reduce(
          items,
          (result, item) => {
            result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
            result += 1;
            return result;
          },
          0
        );

        const filteredEdges = _.filter(unitFlowFeedEdges, edge => {
          const itemId = _.get(edge, "node.item.id");
          return !_.includes(ids, itemId);
        });

        const updatedFeed = {
          ...unitFlowFeed,
          edges: filteredEdges,
          resourceTypeBasedCount: {
            ...unitFlowFeed?.resourceTypeBasedCount,
            totalCount:
              _.get(unitFlowFeed, "resourceTypeBasedCount.totalCount", 0) -
              countOfDeletedItems,
          },
        };

        return update(data, {
          node: {
            unitFlowFeed: {
              unitFlowFlatFeed: { $set: updatedFeed },
            },
          },
        });
      }
    );

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
      }
    );

    cache.updateQuery(
      {
        query: getUnitCombinedLibraryQuery,
        variables: combinedLibraryFeedQueryVariables,
      },
      data => {
        if (!data) return;

        const oldEdges = _.cloneDeep(
          _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
        );

        const updatedEdges = _.filter(
          oldEdges,
          ({ node }) => !_.includes(ids, node.item?.id)
        );

        return update(data, {
          node: {
            unitFlowFeed: {
              unitFlowFlatFeed: {
                edges: { $set: updatedEdges },
              },
            },
          },
        });
      }
    );
  };
};

// only be used in learning course flow
export const shareUnitFlowContent = () => {};

export const handleQuickCreateAndEdit = ({
  item,
  isSaving = true,
  quickCreation = false,
  setNewDataIds,
  isInlineCreation = true,
  showRightPaneLoader,
  setIsCombinedLibraryActionLoading,
  templateId,
  taskTypeId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      type,
      putAfterItemId,
      label,
      isFolderCollapsed,
      sectionCreationType,
      parent,
      depth,
      shouldAddToBottom = false,
    } = item;

    if (isSaving && (Boolean(label.trim()) || !isInlineCreation)) {
      if (!quickCreation) {
        dispatch(
          setActiveContentData({
            ...item,
            isSavingInline: true,
          })
        );
      }

      const parentId = parent?.id;

      switch (type) {
        case "CREATE_SECTION": {
          if (quickCreation) {
            dispatch(getResourceCreationToast({}));
          }
          dispatch(
            createUnitFlowResources({
              putAfterItemId,
              setNewDataIds,
              resources: [
                {
                  label,
                  itemType: "FOLDER",
                },
              ],
              isFolderCollapsed,
              quickCreation,
              sectionCreationType,
              parentId,
              newDepth: depth,
              shouldAddToBottom,
            })
          );

          break;
        }

        case "FILE_RENAME":
        case "SECTION_RENAME": {
          dispatch(
            updateUnitFlowResources({
              id: item.id,
              label,
              resourceType: type == "FILE_RENAME" ? "FILE" : "SECTION",
              attachment: _.get(item, "attachment", null),
            })
          );
          break;
        }
        default: {
          if (item?.label || !isInlineCreation) {
            if (quickCreation && isInlineCreation) {
              dispatch(getAssessmentCreationToast());
            }
            const unitPlanId = _.get(
              state,
              "planner.unitPlanData.unitPlanId",
              ""
            );
            const selectedCurriculum = getCurrentCurriculumProgramType(state);
            const isDP = _.isEqual(CURRICULUM_TYPE_DP, selectedCurriculum);
            const academicYearId = getSelectedAcademicYearId(state);
            const courseGradeFilters = {
              academicYearId,
            };
            const course = getCourseDetailsFromCache({
              id: item.courseId,
              courseGradeFilters,
            });
            try {
              const assessmentType = _.get(item, "item.assessmentType", {});
              const groupType = _.get(item, "item.type", null);
              const fields = [];
              if (isDP && groupType !== "QUICK_TASK") {
                const subjectLevels = _.filter(
                  _.get(course, "genericTags", []),
                  {
                    type: "SUBJECT_LEVEL",
                  }
                );
                if (_.size(subjectLevels) == 1) {
                  fields.push({
                    uid: "subjectLevel",
                    value: [_.get(subjectLevels[0], "id", null)],
                  });
                }
              }
              const assessment = await dispatch(
                getCreateAssessmentInputData({
                  assessmentType: _.get(assessmentType, "value", "le"),
                  title: item?.label,
                  groupType,
                  unitPlanObj: {
                    unitPlanId,
                  },
                  course,
                  fields,
                  templateId,
                  taskTypeId,
                })
              );

              if (assessment) {
                return dispatch(
                  createUnitFlowResources({
                    putAfterItemId,
                    setNewDataIds,
                    resources: [
                      {
                        label,
                        itemType: "ASSESSMENT",
                        itemInput: {
                          assessment,
                        },
                      },
                    ],
                    isFolderCollapsed,
                    quickCreation,
                    sectionCreationType,
                    parentId,
                    newDepth: depth,
                    shouldAddToBottom,
                    showLoader: !isInlineCreation && !showRightPaneLoader,
                    showToast: isInlineCreation,
                    showRightPaneLoader,
                    setIsCombinedLibraryActionLoading,
                  })
                );
              }
            } catch (e) {
              dispatch(setActiveContentData({}));
              console.error(e);
            }
          }
        }
      }
    } else {
      dispatch(setActiveContentData({}));
    }
  };
};

export const duplicateUnitFlowResources = args => {
  return async (dispatch, getState) => {
    const {
      id,
      label,
      depth,
      parentId,
      putAfterItemId,
      setNewDataIds,
      addToTop = false,
      showRightPaneLoader = false,
      setIsCombinedLibraryActionLoading = () => {},
    } = args;
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const curriculumType = getCurrentCurriculumProgramType(state);
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    // variables for updating right pane
    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
      }
    );
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const unitFlowFeed = _.get(
      getEntireUnitFlowFeedQueryFromCache(feedQueryVariables),
      "node.unitFlowFeed",
      {}
    );

    const rightPaneQueryData = cache.readQuery({
      query: getUnitCombinedLibraryQuery,
      variables: combinedLibraryFeedQueryVariables,
    });

    const unitFlowIdFromRightPane = _.get(
      rightPaneQueryData,
      "node.unitFlowFeed.unitFlowId",
      ""
    );
    const unitFlowId = unitFlowFeed?.unitFlowId || unitFlowIdFromRightPane;

    const allItems =
      _.get(unitFlowFeed, "unitFlowFlatFeed.edges") ||
      _.get(rightPaneQueryData, "node.unitFlowFeed.unitFlowFlatFeed.edges", []);

    const item = _.find(allItems, ["node.id", id]);
    // we will return if the item is not there in cache of middlePane or rightPane
    if (!item) return;

    const _parentId = _.has(args, "parentId")
      ? parentId
      : _.get(item, "node.parent.id");
    const _depth = _.has(args, "depth") ? depth : _.get(item, "node.depth", 1);
    const _putAfterItemId = _.has(args, "putAfterItemId") ? putAfterItemId : id;

    const input = {
      sourceId: unitPlanId,
      destinationId: unitFlowId,
      sourceModuleType: "UNIT_FLOW",
      selectAll: false,
      includedIds: [id],
      excludedIds: [],
      parentId: _parentId,
      putAfterItemId: _putAfterItemId,
      label,
      addToTop,
    };

    if (showRightPaneLoader) {
      setIsCombinedLibraryActionLoading(true);
    } else {
      dispatch(setIsActionLoading(true));
    }

    try {
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );
      const unitFlowFeed = _.cloneDeep(
        _.get(cachedData, "node.unitFlowFeed.unitFlowFlatFeed", {})
      );
      const { data } = await client.mutate({
        mutation: importEntityFlowResourceMutation,
        variables: {
          input: { ...input, maxDepth: MAX_DEPTH },
          filters: _.get(feedQueryVariables, "filters", {}),
        },
        update: (
          cache,
          {
            data: {
              planner: { importEntityFlowResource },
            },
          }
        ) => {
          if (!importEntityFlowResource?.isSuccess) return;
          const resource = _.get(
            importEntityFlowResource,
            "entityFlowResource",
            {}
          );
          const resourceType = _.get(resource, "resourceType", "");

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "EntityFlowResourceEdge",
                node: {
                  ..._.get(importEntityFlowResource, "entityFlowResource", {}),
                  depth: _depth,
                },
              };

              const parent = _.find(
                unitFlowFeed.edges,
                ({ node }) => node.id === parentId
              )?.node;

              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "EntityFlowResource",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor: 1,
                });
              }

              // If putAfterItemId is provided , add after that item
              if (_putAfterItemId) {
                const itemIndex = _.findIndex(unitFlowFeed.edges, [
                  "node.id",
                  _putAfterItemId,
                ]);

                if (itemIndex !== -1) {
                  unitFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);
                }
              } else {
                // Otherwise, handle based on addToTop flag

                const edges = unitFlowFeed.edges;
                unitFlowFeed.edges = addToTop
                  ? [repeatedItem, ...edges]
                  : [...edges, repeatedItem];
              }

              const updatedFeed = {
                ...unitFlowFeed,
                resourceBasedCount: {
                  ...unitFlowFeed.resourceBasedCount,
                  totalCount:
                    _.get(unitFlowFeed, "resourceBasedCount.totalCount", 0) + 1,
                },
              };

              setNewDataIds?.([resource.id]);
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: combinedLibraryFeedQueryVariables,
            },
            data => {
              if (!data) return;

              const oldEdges = _.cloneDeep(
                _.get(data, "node.unitFlowFeed.unitFlowFlatFeed.edges", [])
              );
              let updatedEdges = [];

              if (resourceType === "ASSESSMENT") {
                const newEdge = {
                  node: convertToCombinedLibraryResource({
                    resource: resource,
                  }),
                  __typename: "EntityFlowResourceEdge",
                };
                updatedEdges = [newEdge, ...oldEdges];
              } else {
                const newEdge = {
                  node: convertToCombinedLibraryResource({
                    resource: resource,
                  }),
                  __typename: "EntityFlowResourceEdge",
                };
                updatedEdges = [newEdge, ...oldEdges];
              }

              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });

      const response = _.get(data, "planner.importEntityFlowResource", false);

      if (response) {
        const { isSuccess } = response;

        if (!isSuccess) {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:something_went_wrong",
              position: "toast-bottom-left",
            })
          );
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      if (showRightPaneLoader) {
        setIsCombinedLibraryActionLoading(false);
      } else {
        dispatch(setIsActionLoading(false));
      }
    }
  };
};

export const repeatItem = args => {
  return async (dispatch, getState) => {
    const {
      item,
      depth,
      parentId,
      putAfterItemId,
      setNewDataIds,
      addToTop = false,
    } = args;
    const id = _.get(item, "id", "");
    const _depth = _.has(args, "depth") ? depth : _.get(item, "depth", 1);
    const _parentId = _.has(args, "parentId")
      ? parentId
      : _.get(item, "parent.id", null);
    const _putAfterItemId = _.has(args, "putAfterItemId") ? putAfterItemId : id;
    if (!id) return;

    const state = getState();
    const filters = _.get(state, "unitFlow.filters", {});
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const input = {
      id,
      parentId: _parentId,
      putAfterItemId: _putAfterItemId,
      addToTop,
    };

    const cachedData = getEntireUnitFlowFeedQueryFromCache(feedQueryVariables);
    const cachedUnitFlowFeed = _.get(
      cachedData,
      "node.unitFlowFeed.unitFlowFlatFeed",
      {}
    );

    const cachedParent = getUnitFlowItemFromCache({
      id: _parentId,
      variables: feedQueryVariables,
    });

    const optimisticNode = {
      ..._.omit(item, "attachment"),
      id: generateRandomId(),
      parent: cachedParent,
      isRepeated: true,
      childrenV2: [],
      repeatedResources: [],
      displaySequence: 0,
      depth: null,
      item: {
        ..._.get(item, "item", {}),
        type: "",
      },
    };

    /*
      Writes the optimistic node in cache
      Prevents skeleton in case of optimstic response
    */
    writeUnitFlowItemInCache({
      id: optimisticNode.id,
      variables: feedQueryVariables,
      data: optimisticNode,
    });

    dispatch(setIsDragDisabled(true));

    const variables = {
      input,
      filters: _.get(feedQueryVariables, "filters", {}),
    };

    try {
      await client.mutate({
        mutation: repeatEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          planner: {
            repeatEntityFlowResource: {
              isSuccess: true,
              error: "",
              entityFlowResource: optimisticNode,
              __typename: "EntityFlowResourceSuccessErrorResponse",
            },
            __typename: "PlannerMutations",
          },
          __typename: "Mutation",
        },
        update: (
          cache,
          {
            data: {
              planner: { repeatEntityFlowResource },
            },
          }
        ) => {
          if (
            _.isEmpty(cachedUnitFlowFeed) ||
            _.isEmpty(repeatEntityFlowResource)
          )
            return;

          const unitFlowFeed = _.cloneDeep(cachedUnitFlowFeed);

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "EntityFlowResourceEdge",
                node: {
                  ..._.get(repeatEntityFlowResource, "entityFlowResource", {}),
                  depth: _depth,
                },
              };

              const parent = _.cloneDeep(cachedParent);
              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "EntityFlowResource",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor: 1,
                });
              }

              const sourceResourceEdge = findSourceResource({
                unitFlowEdges: unitFlowFeed.edges,
                itemId: _.get(item, "item.id"),
              });

              if (sourceResourceEdge) {
                const repeatedItemId = _.get(repeatedItem, "node.id", "");
                sourceResourceEdge.node.repeatedResources.push(repeatedItemId);
              }

              // If putAfterItemId is provided , add after that item
              if (_putAfterItemId && !addToTop) {
                const itemIndex = _.findIndex(unitFlowFeed.edges, [
                  "node.id",
                  _putAfterItemId,
                ]);

                if (itemIndex !== -1) {
                  unitFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);
                }
              } else {
                // Otherwise, handle based on addToTop flag

                unitFlowFeed.edges = addToTop
                  ? [repeatedItem, ...unitFlowFeed.edges]
                  : [...unitFlowFeed.edges, repeatedItem];
              }

              const updatedFeed = {
                ...unitFlowFeed,
                resourceBasedCount: {
                  ...unitFlowFeed.resourceBasedCount,
                  totalCount:
                    _.get(unitFlowFeed, "resourceBasedCount.totalCount", 0) + 1,
                },
              };

              setNewDataIds?.([_.get(repeatedItem, "node.id", "")]);
              return update(data, {
                node: {
                  unitFlowFeed: {
                    unitFlowFlatFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });

      const assessmentLabel = _.get(item, "item.title.value", "");

      dispatch(
        setToastMsg({
          type: "info",
          msg: "toastMsgs:connected_view_added_to_unit_flow",
          hasHTMLTags: true,
          showIcon: false,
          locale_params: [
            {
              key: "label",
              value: assessmentLabel,
              isPlainText: true,
            },
          ],
        })
      );
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      dispatch(setIsDragDisabled(false));
    }
  };
};

export const importResourcesFromUnitFlow = ({
  input,
  maxDepth,
  showRightPaneLoader,
  setIsCombinedLibraryActionLoading,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const curriculumType = getCurrentCurriculumProgramType(state);

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    dispatch(setIsActionLoading(true));
    if (showRightPaneLoader) {
      setIsCombinedLibraryActionLoading(true);
    }
    const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
    });

    const isRightPaneOpen =
      _.get(state, "unitPlans.customRightPane", "") === "COMBINED_LIBRARY";
    // variables for updating right pane
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
      }
    );

    try {
      const refetchQueries = [
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        {
          query: getUnitFlowOutlineFeedQuery,
          variables: outlineQueryVariables,
        },
      ];
      // Only add combined library query if right pane is open
      if (isRightPaneOpen) {
        refetchQueries.push({
          query: getUnitCombinedLibraryQuery,
          variables: combinedLibraryFeedQueryVariables,
        });
      }

      const putAfterItemId = _.get(input, "putAfterItemId", null);

      const defaultPositioningOfNewItems = _.get(
        state,
        "unitFlow.displaySettings.add_to_top",
        false
      );

      const _addToTop = shouldAddToBottom
        ? false
        : defaultPositioningOfNewItems;

      const shouldAddtoTop = _addToTop && !putAfterItemId;

      const { data } = await client.mutate({
        mutation: importEntityFlowResourceMutation,
        variables: {
          input: {
            ...input,
            addToTop: shouldAddtoTop,
            maxDepth: MAX_DEPTH,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });

      const response = _.get(data, "planner.importEntityFlowResource", false);

      if (response) {
        const { isSuccess, error } = response;
        if (!isSuccess) {
          if (error == "IMPORT_DEPTH_ERROR") {
            dispatch(
              setToastMsg({
                msg: "classMaterials:disabled_overlay_text",
                locale_params: [
                  {
                    key: "maxDepth",
                    value: maxDepth,
                    isPlainText: true,
                  },
                ],
                position: "toast-bottom-left",
              })
            );
          } else {
            dispatch(
              setToastMsg({
                msg: "toastMsgs:something_went_wrong",
                position: "toast-bottom-left",
              })
            );
          }
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
      if (showRightPaneLoader) {
        setIsCombinedLibraryActionLoading(false);
      }
    }
  };
};

// only need to fix depth when learning course is enabled
// because only then conflicts arise
export const fixDepthOfNodes = _.noop;

export const updateLockedState = _.noop;
